import type { Store } from "@/api/storeService";

type ExtendedStore = Store & {
  paymentOptions?: string[];
  deliveryGovernorates?: string[];
  workingHours?: string;
};

export type DraftIssue = {
  field: string;
  label: string;
  severity: "required" | "recommended" | "warning";
};

export type DraftValidation = {
  required: DraftIssue[];
  recommended: DraftIssue[];
  warnings: DraftIssue[];
  total: number;
  flags: {
    name: DraftIssue | null;
    logo: DraftIssue | null;
    categories: DraftIssue | null;
    platforms: DraftIssue | null;
    description: DraftIssue | null;
    location: DraftIssue | null;
    tags: DraftIssue | null;
    payment: DraftIssue | null;
    delivery: DraftIssue | null;
    hours: DraftIssue | null;
    rating: DraftIssue | null;
  };
};

const LOW_CONFIDENCE_THRESHOLD = 5;

export function validateDraft(store: ExtendedStore | undefined | null): DraftValidation {
  const required: DraftIssue[] = [];
  const recommended: DraftIssue[] = [];
  const warnings: DraftIssue[] = [];

  const flags: DraftValidation["flags"] = {
    name: null,
    logo: null,
    categories: null,
    platforms: null,
    description: null,
    location: null,
    tags: null,
    payment: null,
    delivery: null,
    hours: null,
    rating: null,
  };

  if (!store) {
    return { required, recommended, warnings, total: 0, flags };
  }

  // Required
  if (!store.name?.trim()) {
    flags.name = { field: "name", label: "Missing store name", severity: "required" };
    required.push(flags.name);
  }
  if (!store.logoUrl?.trim()) {
    flags.logo = { field: "logo", label: "Missing logo", severity: "required" };
    required.push(flags.logo);
  }
  if (!store.categories || store.categories.length === 0) {
    flags.categories = { field: "categories", label: "No categories assigned", severity: "required" };
    required.push(flags.categories);
  }
  const hasPlatform =
    store.platforms?.facebook ||
    store.platforms?.instagram ||
    store.platforms?.whatsapp ||
    store.platforms?.website ||
    store.platforms?.phone;
  if (!hasPlatform) {
    flags.platforms = { field: "platforms", label: "No contact links", severity: "required" };
    required.push(flags.platforms);
  }

  // Recommended
  if (!store.description?.trim()) {
    flags.description = { field: "description", label: "Missing description", severity: "recommended" };
    recommended.push(flags.description);
  }
  if (!store.location?.trim()) {
    flags.location = { field: "location", label: "Missing location", severity: "recommended" };
    recommended.push(flags.location);
  }
  if (!store.tags || store.tags.length === 0) {
    flags.tags = { field: "tags", label: "No tags", severity: "recommended" };
    recommended.push(flags.tags);
  }
  if (!store.paymentOptions || store.paymentOptions.length === 0) {
    flags.payment = { field: "payment", label: "Payment options not set", severity: "recommended" };
    recommended.push(flags.payment);
  }
  if (!store.deliveryGovernorates || store.deliveryGovernorates.length === 0) {
    flags.delivery = { field: "delivery", label: "Delivery areas not set", severity: "recommended" };
    recommended.push(flags.delivery);
  }
  if (!store.workingHours?.trim()) {
    flags.hours = { field: "hours", label: "Working hours not set", severity: "recommended" };
    recommended.push(flags.hours);
  }

  // Warnings (low-confidence + rating)
  const lowConfCount = (store.categories || []).filter(
    (c) => (c.relevanceScore ?? 10) < LOW_CONFIDENCE_THRESHOLD
  ).length;
  if (lowConfCount > 0) {
    const issue: DraftIssue = {
      field: "categories",
      label: `${lowConfCount} low-confidence categor${lowConfCount === 1 ? "y" : "ies"} (<${LOW_CONFIDENCE_THRESHOLD}/10)`,
      severity: "warning",
    };
    if (!flags.categories) flags.categories = issue;
    warnings.push(issue);
  }
  if ((store.yetfarragRating ?? 0) === 0) {
    flags.rating = { field: "rating", label: "Yetfarrag rating not set", severity: "warning" };
    warnings.push(flags.rating);
  }

  return {
    required,
    recommended,
    warnings,
    total: required.length + recommended.length + warnings.length,
    flags,
  };
}
